// ── Execution Ready Lock (Phase 1 Dispatch Lock) ──
// Sales → Execution boundary lock. Composes existing canonical readiness
// (readiness.ts) and assignment-time guards (payProfileGuard) into a single
// gate that must pass before a job can be dispatched.
//
// DESIGN CONSTRAINTS (locked for Phase 1):
//   • Lock is one-way. Once set, cannot be cleared in v1.
//   • No admin override path.
//   • No auto-mark, no bypass.
//   • factorsAtLock preserves what was true at lock time. Post-lock
//     regressions (kit pulled, contractor falls off, schedule slips) are
//     a known limitation — Phase 1.5 will design invalidation/override.
//   • This module DOES NOT mutate readiness, JobSpec, kits, or pay profiles.
//     It only reads them and composes the verdict.

import type { Job, Contractor } from "./types";
import type { MaterialKit } from "./materialKit";
import type { ContractorPayProfile } from "./contractorPayProfile";
import type { BuildReviewEvent } from "./buildReviewTypes";
import { deriveReadinessFromJob } from "./readiness";
import { assertContractorAssignable } from "./payProfileGuard";

export interface CanMarkJobReadyArgs {
  job: Job;
  kit: MaterialKit | undefined;
  payProfiles: ContractorPayProfile[];
  contractors: Contractor[];
  buildReviewEvents: BuildReviewEvent[];
}

export interface CanMarkJobReadyResult {
  ok: boolean;
  /** All blocking reasons. Never short-circuits. */
  reasons: string[];
  /** Snapshot of the readiness factors evaluated at lock time. */
  factors: string[];
}

/**
 * Verdict-only check. Returns ALL blocking reasons (does not short-circuit)
 * so the UI can show the full list.
 *
 * Composition:
 *   1. Canonical readiness (readiness.ts) — overall must be "ready".
 *      That already includes JobSpec Bound + Materials/Kit fold.
 *   2. Pay Profile eligibility — assignment-time gate. Only checked when a
 *      contractor is currently assigned (otherwise Pay Profile applies at
 *      the point of assignment, not at the point of marking ready).
 */
export function canMarkJobReady(args: CanMarkJobReadyArgs): CanMarkJobReadyResult {
  const { job, kit, payProfiles, contractors, buildReviewEvents } = args;
  const reasons: string[] = [];
  const factors: string[] = [];

  // ── 1. Canonical readiness ──
  const derived = deriveReadinessFromJob(job, buildReviewEvents, kit);
  const items = [
    derived.jobSpec,
    derived.permit,
    derived.materials,
    derived.crew,
    derived.schedule,
    derived.site,
    derived.financial,
    derived.buildReview,
  ];
  for (const item of items) {
    factors.push(`${item.label}: ${item.status}`);
    if (item.status === "blocked") {
      reasons.push(`${item.label} — ${item.shortText}`);
    } else if (item.status === "at_risk" || item.status === "pending") {
      reasons.push(`${item.label} — ${item.shortText} (not ready)`);
    }
  }

  // ── 2. Pay Profile (assignment-time gate) ──
  // Only enforced when a contractor is bound to the job. When no contractor
  // is yet assigned, Crew readiness above will already block the lock.
  if (job.assignedSubcontractor) {
    const payCheck = assertContractorAssignable({
      contractorName: job.assignedSubcontractor,
      contractors,
      projectType: job.projectType,
      payProfiles,
    });
    factors.push(`Pay Profile: ${payCheck.allowed ? "ok" : "blocked"}`);
    if (!payCheck.allowed) {
      reasons.push(`Pay Profile — ${payCheck.reason}`);
    }
  } else {
    factors.push("Pay Profile: n/a (no sub assigned)");
  }

  return { ok: reasons.length === 0, reasons, factors };
}

/**
 * Pure mutation: returns the next Job with executionReady set, or returns
 * the same Job unchanged when the gate is not satisfied.
 *
 * Caller is responsible for routing this through setJobs(). The mutation
 * never invalidates or replaces an existing lock — once locked, always locked
 * in v1.
 */
export function markJobReady(
  job: Job,
  operatorName: string,
  verdict: CanMarkJobReadyResult,
  now: Date = new Date(),
): { ok: boolean; job: Job; reason?: string } {
  if (job.executionReady) {
    return { ok: true, job }; // already locked — no-op
  }
  if (!verdict.ok) {
    return { ok: false, job, reason: "Job is not ready for execution." };
  }
  if (!operatorName.trim()) {
    return { ok: false, job, reason: "Operator name is required." };
  }
  return {
    ok: true,
    job: {
      ...job,
      executionReady: {
        markedAt: now.toISOString(),
        markedBy: operatorName.trim(),
        factorsAtLock: verdict.factors,
      },
    },
  };
}

/** Predicate used by dispatch sites — the cheap, lock-first check. */
export function isExecutionReady(job: Job): boolean {
  return !!job.executionReady;
}

/**
 * Status values in factorsAtLock strings (format: "Label: status") that
 * count as "passing" at lock time. Anything else means the factor was
 * recorded in a non-passing state and we don't compare it for staleness.
 *
 * Canonical readiness items emit `ready | pending | at_risk | blocked`.
 * Pay Profile emits `ok | blocked | n/a (no sub assigned)`.
 */
const PASSING_FACTOR_STATUSES = new Set(["ready", "ok"]);

function parseFactor(entry: string): { label: string; status: string } | null {
  const idx = entry.indexOf(":");
  if (idx === -1) return null;
  const label = entry.slice(0, idx).trim();
  const status = entry.slice(idx + 1).trim().toLowerCase();
  // Strip trailing parenthetical e.g. "n/a (no sub assigned)" -> "n/a"
  const head = status.split(/\s+/)[0] ?? status;
  return { label, status: head };
}

/**
 * Lock Stale detector — strict, factor-level, deterministic.
 *
 * Returns true iff:
 *   1. job.executionReady exists, AND
 *   2. at least one factor recorded in factorsAtLock as "passing" (ready/ok)
 *      no longer passes under the current canonical readiness verdict.
 *
 * Forward-compatible: only factors present in factorsAtLock are compared.
 * Any new readiness factor introduced after the lock was set is ignored,
 * so historical locks don't retroactively fail because the model evolved.
 *
 * No mutation. No auto-invalidation. Stale is a SIGNAL, not an action.
 */
export function isLockStale(job: Job, args: CanMarkJobReadyArgs): boolean {
  if (!job.executionReady) return false;
  const recorded = job.executionReady.factorsAtLock ?? [];
  if (recorded.length === 0) return false;

  const current = canMarkJobReady(args);
  const currentByLabel = new Map<string, string>();
  for (const entry of current.factors) {
    const parsed = parseFactor(entry);
    if (parsed) currentByLabel.set(parsed.label, parsed.status);
  }

  for (const entry of recorded) {
    const parsed = parseFactor(entry);
    if (!parsed) continue;
    if (!PASSING_FACTOR_STATUSES.has(parsed.status)) continue; // wasn't passing at lock time
    const now = currentByLabel.get(parsed.label);
    if (now === undefined) continue; // factor no longer present in model — forward-compatible skip
    if (!PASSING_FACTOR_STATUSES.has(now)) return true;
  }
  return false;
}

export const EXECUTION_LOCK_BLOCK_TITLE = "Job is not marked Ready for Execution.";
export const EXECUTION_LOCK_BLOCK_DESCRIPTION =
  "Open the Install Readiness panel and resolve outstanding factors, then mark the job ready.";

export function getExecutionLockBlockDescription(verdict?: Pick<CanMarkJobReadyResult, "ok" | "reasons">): string {
  if (!verdict) return EXECUTION_LOCK_BLOCK_DESCRIPTION;
  if (verdict.ok) {
    return "All readiness checks pass, but the job still needs to be marked Ready for Execution in the Install Readiness panel.";
  }
  const reasons = verdict.reasons.slice(0, 3).join(" · ");
  return reasons || EXECUTION_LOCK_BLOCK_DESCRIPTION;
}

export function openInstallReadinessPanel(): void {
  if (typeof window === "undefined") return;
  window.location.assign("/execution?tab=schedule&sub=readiness");
}

export function getExecutionLockToastOptions(description = EXECUTION_LOCK_BLOCK_DESCRIPTION) {
  return {
    description,
    duration: 8000,
    action: {
      label: "Open Readiness",
      onClick: openInstallReadinessPanel,
    },
  };
}
